import * as t from 'io-ts';
import _, {compact, isString, isUndefined} from 'lodash';
import {pathToRegexp} from 'path-to-regexp';

import {parseQueryString, QueryStringArguments} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {isDefined, isInteger} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {isTeammateNamespace} from '../../helpers/models/namespaceHelpers';
import {
  RRuleBundleCategoriesEnum,
  RSettingsRuleListFiltersState,
  SettingsRuleListFiltersState,
} from '../../settings/rules/list/ruleBundleCategory';
import {makeEnumRuntimeType, RLocalApplicationIdRef} from '../api/apiTypes';
import {RIntegrationTypeCategory} from '../api/integrationTypesModel';
import {RLocalApplicationFeature} from '../api/localApplicationModel';

/*
 * Runtime types.
 */

export enum SettingsRootPagesEnum {
  TAGS = 'tags',
  RULES = 'rules',
  USER_HOME = 'user-home',
  ADMIN_HOME = 'home',
  CUSTOM_FIELDS = 'custom-fields',
  TEAMMATES = 'teammates',
  SIGNATURES = 'signatures',
  ANSWERS = 'answers',
  INBOXES = 'inboxes',
  USER_PREFERENCES = 'user-preferences',
  SNOOZES = 'snoozes',
  APPLICATIONS = 'applications',
  CIRCLES = 'circles',
  PROFILE = 'profile',
  APPS = 'apps',
  VACATION_RESPONDERS = 'responders',
  NOTIFICATIONS = 'notifications',
  DEVELOPERS = 'developers',
}

export enum SettingsResourcePagesEnum {
  OVERVIEW = 'overview',
  RECIPES = 'recipes',
  EDIT = 'edit',
  NEW = 'new',

  LIBRARY = 'library',
  LOAD_BALANCING = 'limits',

  IMPORT_CUSTOM_ATTRIBUTES = 'import-custom-attributes',
}

export enum SettingsInboxesSubPagesEnum {
  OVERVIEW = 'overview',
  CHANNELS = 'channels',
  NEW_CHANNEL = 'channels/newChannel',
  RULES = 'rules',
  TEAMMATE_ACCESS = 'access',
  SETTINGS = 'settings',
  BUSINESS_HOURS = 'business-hours',
  CUSTOM_ATTRIBUTES = 'custom-attributes',
}

export enum SettingsTabsPagesEnum {
  ALL = 'all',
  ARCHIVED = 'archived',
  ADVANCED = 'advanced',
  BASIC = 'basic',
}

export enum SettingsCustomFieldsPagesEnum {
  TEAMMATES = 'team',
  INBOXES = 'inboxes',
  CARDS = 'cards',
  ACCOUNTS = 'accounts',
}

export enum SettingsTeammatesSubPagesEnum {
  RULES = 'rules',
}

export enum SettingsApplicationComponentsEnum {
  DETAILS = 'details',
  LIST = 'list',
  AUTOLINKERS = 'autolinkers',
  OAUTH_CLIENT = 'oauth_client',
  PREVIEWERS = 'previewers',
  SERVERS = 'servers',
}

export enum SettingsAppsTabsEnum {
  COLLECTIONS = 'collections',
  CATEGORIES = 'categories',
  POPULAR = 'popular',
  PLUGINS = 'plugins',
  CHANNELS = 'channels',
  ENABLED = 'enabled',
  ALL = 'all',
  DETAILS = 'details',
}

export enum SettingsAppsAppDetailsTabsEnum {
  OVERVIEW = 'overview',
  GETTING_STARTED = 'getting-started',
}

export enum SettingsDevelopersPagesEnum {
  APPLICATIONS = 'applications',
  TOKENS = 'tokens',
  APPLICATION_INFO = 'application-info',
  APPLICATION_FEATURES = 'application-features',
  APPLICATION_OAUTH = 'application-oauth',
  APPLICATION_SERVERS = 'application-servers',
}

const RSettingsRootPage = makeEnumRuntimeType(SettingsRootPagesEnum);
const RSettingsAppsTab = makeEnumRuntimeType(SettingsAppsTabsEnum);
const RSettingsAppsDetailsTab = makeEnumRuntimeType(SettingsAppsAppDetailsTabsEnum);
const RSettingsResourcePage = makeEnumRuntimeType(SettingsResourcePagesEnum);
const RSettingsTabsPage = makeEnumRuntimeType(SettingsTabsPagesEnum);
const RSettingsTeammatesSubPage = makeEnumRuntimeType(SettingsTeammatesSubPagesEnum);
const RSettingsInboxesSubPage = makeEnumRuntimeType(SettingsInboxesSubPagesEnum);
export const RSettingsCustomFieldPage = makeEnumRuntimeType(SettingsCustomFieldsPagesEnum);
export const RSettingsDevelopersPage = makeEnumRuntimeType(SettingsDevelopersPagesEnum);
const RSettingsUrlBase = t.type({
  rootPage: RSettingsRootPage,
});

const RSettingsTagsUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    tagId: t.union([t.undefined, t.number]),
    rootPage: t.literal(SettingsRootPagesEnum.TAGS),
    resourcePage: RSettingsResourcePage,
    tabPage: t.union([t.undefined, RSettingsTabsPage]),
    searchQuery: t.string,
  }),
]);

const RSettingsRulesUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    rootPage: t.literal(SettingsRootPagesEnum.RULES),
    resourcePage: RSettingsResourcePage,
    isInboxCreation: t.boolean,
    inboxId: t.union([t.undefined, t.number]),
    ruleId: t.union([t.undefined, t.number]),
    recipe: t.union([t.undefined, t.string]),
    bundleId: t.union([t.undefined, t.number]),
    filters: t.union([t.undefined, RSettingsRuleListFiltersState]),
  }),
]);

const RSettingsTeammateUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.TEAMMATES),
    resourcePage: RSettingsResourcePage,
    namespace: t.string,
  }),
  t.partial({
    subPage: RSettingsTeammatesSubPage,
    subResourcePage: RSettingsResourcePage,
    teammateAlias: t.string,
    ruleId: t.number,
  }),
]);

const RSettingsCustomFieldsUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    customFieldId: t.union([t.undefined, t.number]),
    rootPage: t.literal(SettingsRootPagesEnum.CUSTOM_FIELDS),
    resourcePage: RSettingsResourcePage,
    tabPage: RSettingsCustomFieldPage,
  }),
]);

const RSettingsSignaturesUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    signatureId: t.union([t.undefined, t.number]),
    rootPage: t.literal(SettingsRootPagesEnum.SIGNATURES),
    resourcePage: RSettingsResourcePage,
  }),
]);

const RSettingsAnswersUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    answerId: t.union([t.undefined, t.number]),
    folderId: t.union([t.undefined, t.number]),
    rootPage: t.literal(SettingsRootPagesEnum.ANSWERS),
    inFolder: t.boolean,
    resourcePage: RSettingsResourcePage,
  }),
]);

const RSettingsInboxesUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    inboxId: t.union([t.undefined, t.number]),
    rootPage: t.literal(SettingsRootPagesEnum.INBOXES),
    resourcePage: RSettingsResourcePage,
  }),
  t.partial({
    subPage: RSettingsInboxesSubPage,
    channelId: t.number,
  }),
]);

const RSettingsPreferencesUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    rootPage: t.literal(SettingsRootPagesEnum.USER_PREFERENCES),
    initialPreference: t.string,
  }),
]);

const RSettingsSnoozesUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    rootPage: t.literal(SettingsRootPagesEnum.SNOOZES),
    resourcePage: RSettingsResourcePage,
    snoozeId: t.union([t.undefined, t.number]),
  }),
]);

const RSettingsVacationRespondersUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    namespace: t.string,
    rootPage: t.literal(SettingsRootPagesEnum.VACATION_RESPONDERS),
    resourcePage: RSettingsResourcePage,
    vacationResponderId: t.union([t.undefined, t.number]),
  }),
]);

const RSettingsUserHomeUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.USER_HOME),
  }),
]);
const RSettingsAdminHomeUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.ADMIN_HOME),
  }),
]);

const RSettingsBaseApplicationsUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.APPLICATIONS),
    resourcePage: t.union([t.undefined, RSettingsResourcePage]),
    searchQuery: t.union([t.undefined, t.string]),
  }),
]);

const RSettingsApplicationsDetailsUrl = t.intersection([
  RSettingsBaseApplicationsUrl,
  t.type({
    componentPage: t.literal(SettingsApplicationComponentsEnum.DETAILS),
    applicationId: t.union([t.number, t.undefined]),
  }),
]);

const RSettingsApplicationsListUrl = t.intersection([
  RSettingsBaseApplicationsUrl,
  t.type({
    componentPage: t.literal(SettingsApplicationComponentsEnum.LIST),
    applicationId: t.undefined,
  }),
]);

const RSettingsAutolinkersUrl = t.intersection([
  RSettingsBaseApplicationsUrl,
  t.type({
    componentPage: t.literal(SettingsApplicationComponentsEnum.AUTOLINKERS),
    applicationId: t.number,
    autolinkerId: t.union([t.undefined, t.number]),
  }),
]);

const RSettingsOauthClientUrl = t.intersection([
  RSettingsBaseApplicationsUrl,
  t.type({
    componentPage: t.literal(SettingsApplicationComponentsEnum.OAUTH_CLIENT),
    applicationId: t.number,
  }),
]);

const RSettingsPreviewersUrl = t.intersection([
  RSettingsBaseApplicationsUrl,
  t.type({
    componentPage: t.literal(SettingsApplicationComponentsEnum.PREVIEWERS),
    applicationId: t.number,
    previewerId: t.union([t.undefined, t.number]),
  }),
]);

const RSettingsServersUrl = t.intersection([
  RSettingsBaseApplicationsUrl,
  t.type({
    componentPage: t.literal(SettingsApplicationComponentsEnum.SERVERS),
    applicationId: t.number,
    serverId: t.union([t.undefined, t.number, t.string]),
  }),
]);

const RSettingsApplicationsUrl = t.readonly(
  t.taggedUnion('componentPage', [
    RSettingsApplicationsDetailsUrl,
    RSettingsApplicationsListUrl,
    RSettingsAutolinkersUrl,
    RSettingsOauthClientUrl,
    RSettingsPreviewersUrl,
    RSettingsServersUrl,
  ]),
);

const RSettingsCirclesUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.CIRCLES),
    resourcePage: RSettingsResourcePage,
    circleId: t.union([t.undefined, t.Integer]),
  }),
]);

const RSettingsProfileUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.PROFILE),
  }),
]);

/*
 * Apps Urls.
 */

const RSettingsBaseAppsUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.APPS),
    searchQuery: t.union([t.undefined, t.string]),
    previousUrl: t.union([t.undefined, t.string]),
    source: t.union([t.undefined, t.string]),
  }),
]);

const RSettingsAppsCollectionsUrl = t.intersection([
  RSettingsBaseAppsUrl,
  t.type({
    tabPage: t.literal(SettingsAppsTabsEnum.COLLECTIONS),
  }),
]);

const RSettingsAppsCategoriesUrl = t.intersection([
  RSettingsBaseAppsUrl,
  t.type({
    tabPage: t.literal(SettingsAppsTabsEnum.CATEGORIES),
  }),
  t.partial({
    category: RIntegrationTypeCategory,
  }),
]);

const RSettingsAppsPopularUrl = t.intersection([
  RSettingsBaseAppsUrl,
  t.type({
    tabPage: t.literal(SettingsAppsTabsEnum.POPULAR),
  }),
]);

const RSettingsAppsPluginsUrl = t.intersection([
  RSettingsBaseAppsUrl,
  t.type({
    tabPage: t.literal(SettingsAppsTabsEnum.PLUGINS),
  }),
]);

const RSettingsAppsChannelsUrl = t.intersection([
  RSettingsBaseAppsUrl,
  t.type({
    tabPage: t.literal(SettingsAppsTabsEnum.CHANNELS),
  }),
]);

const RSettingsAppsEnabledUrl = t.intersection([
  RSettingsBaseAppsUrl,
  t.type({
    tabPage: t.literal(SettingsAppsTabsEnum.ENABLED),
  }),
]);

const RSettingsAppsAllUrl = t.intersection([
  RSettingsBaseAppsUrl,
  t.type({
    tabPage: t.literal(SettingsAppsTabsEnum.ALL),
  }),
]);

const RSettingsAppsAppDetailsUrl = t.intersection([
  RSettingsBaseAppsUrl,
  t.type({
    tabPage: t.literal(SettingsAppsTabsEnum.DETAILS),
    appId: t.string,
    detailsPage: RSettingsAppsDetailsTab,
    shouldOpenConfiguration: t.boolean,
  }),
]);

const RSettingsAppsUrl = t.readonly(
  t.taggedUnion('tabPage', [
    RSettingsAppsCollectionsUrl,
    RSettingsAppsCategoriesUrl,
    RSettingsAppsPopularUrl,
    RSettingsAppsPluginsUrl,
    RSettingsAppsChannelsUrl,
    RSettingsAppsEnabledUrl,
    RSettingsAppsAllUrl,
    RSettingsAppsAppDetailsUrl,
  ]),
);

/*
 * Notifications.
 */

const RSettingsNotificationsUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.NOTIFICATIONS),
    resourcePage: RSettingsResourcePage,
    tabPage: t.union([t.undefined, RSettingsTabsPage]),
    notificationIndex: t.union([t.undefined, t.number]),
  }),
  t.partial({
    namespace: t.string,
  }),
]);

/*
 * Developers.
 */

const RSettingsBaseDevelopersUrl = t.intersection([
  RSettingsUrlBase,
  t.type({
    rootPage: t.literal(SettingsRootPagesEnum.DEVELOPERS),
  }),
]);

const RSettingsDevelopersApplicationsUrl = t.intersection([
  RSettingsBaseDevelopersUrl,
  t.type({
    subPage: t.literal(SettingsDevelopersPagesEnum.APPLICATIONS),
  }),
]);

const RSettingsDevelopersTokensUrl = t.intersection([
  RSettingsBaseDevelopersUrl,
  t.type({
    subPage: t.literal(SettingsDevelopersPagesEnum.TOKENS),
  }),
  t.partial({
    tokenId: t.number,
  }),
]);

const RSettingsDevelopersBaseApplicationUrl = t.intersection([
  RSettingsBaseDevelopersUrl,
  t.type({
    applicationId: RLocalApplicationIdRef,
  }),
]);

const RSettingsDevelopersApplicationInfoUrl = t.intersection([
  RSettingsDevelopersBaseApplicationUrl,
  t.type({
    subPage: t.literal(SettingsDevelopersPagesEnum.APPLICATION_INFO),
  }),
]);

const RSettingsDevelopersApplicationFeaturesUrl = t.intersection([
  RSettingsDevelopersBaseApplicationUrl,
  t.type({
    subPage: t.literal(SettingsDevelopersPagesEnum.APPLICATION_FEATURES),
  }),
  t.partial({
    feature: RLocalApplicationFeature,
  }),
]);

const RSettingsDevelopersApplicationOAuthUrl = t.intersection([
  RSettingsDevelopersBaseApplicationUrl,
  t.type({
    subPage: t.literal(SettingsDevelopersPagesEnum.APPLICATION_OAUTH),
  }),
]);

const RSettingsDevelopersApplicationServersUrl = t.intersection([
  RSettingsDevelopersBaseApplicationUrl,
  t.type({
    subPage: t.literal(SettingsDevelopersPagesEnum.APPLICATION_SERVERS),
  }),
  t.partial({
    serverId: t.number,
    isConnecting: t.boolean,
  }),
]);

const RSettingsDevelopersUrl = t.readonly(
  t.taggedUnion('subPage', [
    RSettingsDevelopersApplicationsUrl,
    RSettingsDevelopersTokensUrl,
    RSettingsDevelopersApplicationInfoUrl,
    RSettingsDevelopersApplicationFeaturesUrl,
    RSettingsDevelopersApplicationOAuthUrl,
    RSettingsDevelopersApplicationServersUrl,
  ]),
);

/*
 * Urls.
 */

export const RSettingsUrl = t.readonly(
  t.union([
    RSettingsUserHomeUrl,
    RSettingsAdminHomeUrl,
    RSettingsTagsUrl,
    RSettingsRulesUrl,
    RSettingsCustomFieldsUrl,
    RSettingsSignaturesUrl,
    RSettingsAnswersUrl,
    RSettingsInboxesUrl,
    RSettingsPreferencesUrl,
    RSettingsSnoozesUrl,
    RSettingsApplicationsUrl,
    RSettingsCirclesUrl,
    RSettingsProfileUrl,
    RSettingsAppsUrl,
    RSettingsVacationRespondersUrl,
    RSettingsNotificationsUrl,
    RSettingsDevelopersUrl,
  ]),
);

/*
 * Interface.
 */

export interface SettingsUserHomeUrl extends t.TypeOf<typeof RSettingsUserHomeUrl> {}
export interface SettingsAdminHomeUrl extends t.TypeOf<typeof RSettingsAdminHomeUrl> {}
export interface SettingsTagsUrl extends t.TypeOf<typeof RSettingsTagsUrl> {}
export type SettingsRulesUrl = t.TypeOf<typeof RSettingsRulesUrl>;
export type SettingsCustomFieldsUrl = t.TypeOf<typeof RSettingsCustomFieldsUrl>;
export type SettingsTeammateUrl = t.TypeOf<typeof RSettingsTeammateUrl>;
export interface SettingsSignaturesUrl extends t.TypeOf<typeof RSettingsSignaturesUrl> {}
export interface SettingsAnswersUrl extends t.TypeOf<typeof RSettingsAnswersUrl> {}
export interface SettingsInboxesUrl extends t.TypeOf<typeof RSettingsInboxesUrl> {}
export interface SettingsPreferencesUrl extends t.TypeOf<typeof RSettingsPreferencesUrl> {}
export interface SettingsSnoozesUrl extends t.TypeOf<typeof RSettingsSnoozesUrl> {}
export interface SettingsCircleUrl extends t.TypeOf<typeof RSettingsCirclesUrl> {}
export interface SettingsProfileUrl extends t.TypeOf<typeof RSettingsProfileUrl> {}
export interface SettingsVacationRespondersUrl extends t.TypeOf<typeof RSettingsVacationRespondersUrl> {}
export interface SettingsNotificationsUrl extends t.TypeOf<typeof RSettingsNotificationsUrl> {}

interface SettingsApplicationsBaseUrl extends t.TypeOf<typeof RSettingsBaseApplicationsUrl> {}
export interface SettingsApplicationsDetailsUrl extends t.TypeOf<typeof RSettingsApplicationsDetailsUrl> {}
export interface SettingsApplicationsListUrl extends t.TypeOf<typeof RSettingsApplicationsListUrl> {}
export interface SettingsAutolinkersUrl extends t.TypeOf<typeof RSettingsAutolinkersUrl> {}
export interface SettingsOauthClientUrl extends t.TypeOf<typeof RSettingsOauthClientUrl> {}
export interface SettingsPreviewersUrl extends t.TypeOf<typeof RSettingsPreviewersUrl> {}
export interface SettingsServersUrl extends t.TypeOf<typeof RSettingsServersUrl> {}

interface SettingsAppsBaseUrl extends t.TypeOf<typeof RSettingsBaseAppsUrl> {}
export interface SettingsAppsCollectionsUrl extends t.TypeOf<typeof RSettingsAppsCollectionsUrl> {}
export interface SettingsAppsCategoriesUrl extends t.TypeOf<typeof RSettingsAppsCategoriesUrl> {}
export interface SettingsAppsPopularUrl extends t.TypeOf<typeof RSettingsAppsPopularUrl> {}
export interface SettingsAppsPluginsUrl extends t.TypeOf<typeof RSettingsAppsPluginsUrl> {}
export interface SettingsAppsChannelsUrl extends t.TypeOf<typeof RSettingsAppsChannelsUrl> {}
export interface SettingsAppsEnabledUrl extends t.TypeOf<typeof RSettingsAppsEnabledUrl> {}
export interface SettingsAppsAllUrl extends t.TypeOf<typeof RSettingsAppsAllUrl> {}
export interface SettingsAppsAppDetailsUrl extends t.TypeOf<typeof RSettingsAppsAppDetailsUrl> {}

interface SettingsBaseDevelopersUrl extends t.TypeOf<typeof RSettingsBaseDevelopersUrl> {}
interface SettingsBaseDevelopersApplicationUrl
  extends t.TypeOf<typeof RSettingsDevelopersBaseApplicationUrl> {}
export interface SettingsDevelopersApplicationsUrl
  extends t.TypeOf<typeof RSettingsDevelopersApplicationsUrl> {}
export interface SettingsDevelopersTokensUrl extends t.TypeOf<typeof RSettingsDevelopersTokensUrl> {}
export interface SettingsDevelopersApplicationInfoUrl
  extends t.TypeOf<typeof RSettingsDevelopersApplicationInfoUrl> {}
export interface SettingsDevelopersApplicationFeaturesUrl
  extends t.TypeOf<typeof RSettingsDevelopersApplicationFeaturesUrl> {}
export interface SettingsDevelopersApplicationOAuthUrl
  extends t.TypeOf<typeof RSettingsDevelopersApplicationOAuthUrl> {}
export interface SettingsDevelopersApplicationServersUrl
  extends t.TypeOf<typeof RSettingsDevelopersApplicationServersUrl> {}

export type SettingsApplicationsUrl =
  | SettingsApplicationsDetailsUrl
  | SettingsApplicationsListUrl
  | SettingsAutolinkersUrl
  | SettingsOauthClientUrl
  | SettingsPreviewersUrl
  | SettingsServersUrl;

export type SettingsAppsUrl =
  | SettingsAppsCollectionsUrl
  | SettingsAppsCategoriesUrl
  | SettingsAppsPopularUrl
  | SettingsAppsPluginsUrl
  | SettingsAppsChannelsUrl
  | SettingsAppsEnabledUrl
  | SettingsAppsAllUrl
  | SettingsAppsAppDetailsUrl;

export type SettingsDevelopersUrl =
  | SettingsDevelopersApplicationsUrl
  | SettingsDevelopersTokensUrl
  | SettingsDevelopersApplicationInfoUrl
  | SettingsDevelopersApplicationFeaturesUrl
  | SettingsDevelopersApplicationOAuthUrl
  | SettingsDevelopersApplicationServersUrl;

export type SettingsUrlModel =
  | SettingsUserHomeUrl
  | SettingsAdminHomeUrl
  | SettingsTagsUrl
  | SettingsRulesUrl
  | SettingsCustomFieldsUrl
  | SettingsTeammateUrl
  | SettingsSignaturesUrl
  | SettingsAnswersUrl
  | SettingsInboxesUrl
  | SettingsPreferencesUrl
  | SettingsSnoozesUrl
  | SettingsApplicationsUrl
  | SettingsCircleUrl
  | SettingsProfileUrl
  | SettingsAppsUrl
  | SettingsVacationRespondersUrl
  | SettingsNotificationsUrl
  | SettingsDevelopersUrl;

/*
 * IO methods.
 */

// TODO: Use app-wide router.
export interface SettingsMatchProps {
  /** The path pattern used to match. */
  path: string;
  /** The matched portion of the URL. */
  url: string;
  // TODO: Use runtime types when parsing so we can type this more tightly.
  params: any;
}

const settingsAnswersFolderRE = pathToRegexp('/settings/:namespace/answers/folders/:resourcePage', [], {
  start: true,
  end: false,
});
export function parseSettingsUrl(
  src: SettingsMatchProps,
  userNamespace: string,
): SettingsUrlModel | undefined {
  const rootPage = parseRootPage(src.path);
  const resourcePage = parseResourcePage(src.path);
  const query = parseQueryParams(src);

  switch (rootPage) {
    case SettingsRootPagesEnum.TAGS: {
      // We don't want to allow the user to edit a delegated private tag.
      if (
        resourcePage === SettingsResourcePagesEnum.EDIT &&
        isTeammateNamespace(src.params.namespace) &&
        userNamespace !== src.params.namespace
      ) {
        return undefined;
      }

      return {
        rootPage,
        resourcePage: resourcePage || SettingsResourcePagesEnum.OVERVIEW,
        tagId: (src.params.tagId && Number(src.params.tagId)) || undefined,
        namespace: src.params.namespace,
        tabPage: makeTabPage(query, resourcePage || SettingsResourcePagesEnum.OVERVIEW),
        searchQuery: t.type({search: t.string}).is(query) ? query.search : '',
      };
    }
    case SettingsRootPagesEnum.RULES: {
      const ruleResourcePage = resourcePage || SettingsResourcePagesEnum.OVERVIEW;

      return {
        rootPage,
        resourcePage: ruleResourcePage,
        namespace: src.params.namespace,
        isInboxCreation: false,
        inboxId: undefined,
        ruleId: src.params.ruleId ? Number(src.params.ruleId) : undefined,
        recipe: src.params.recipe || undefined,
        bundleId: parseRuleBundleId(src.params.bundleId),
        filters: parseRuleFilters(ruleResourcePage, query),
      };
    }
    case SettingsRootPagesEnum.CUSTOM_FIELDS: {
      return {
        rootPage,
        namespace: src.params.namespace,
        resourcePage: looselyParseResourcePage(src.path) || SettingsResourcePagesEnum.OVERVIEW,
        customFieldId: src.params.customFieldId ? Number(src.params.customFieldId) : undefined,
        tabPage: src.params.tabPage,
      };
    }

    case SettingsRootPagesEnum.TEAMMATES: {
      return {
        rootPage,
        resourcePage: resourcePage || SettingsResourcePagesEnum.NEW,
        ruleId: src.params.ruleId ? Number(src.params.ruleId) : undefined,
        namespace: src.params.namespace,
        subPage: parseSubPage(src.path, RSettingsTeammatesSubPage),
        subResourcePage: parseSubResource(src.path, RSettingsResourcePage),
        teammateAlias: src.params.teammateAlias,
      };
    }

    case SettingsRootPagesEnum.SIGNATURES: {
      return {
        rootPage,
        resourcePage: resourcePage || SettingsResourcePagesEnum.OVERVIEW,
        namespace: src.params.namespace,
        signatureId: src.params.signatureId ? Number(src.params.signatureId) : undefined,
      };
    }

    case SettingsRootPagesEnum.ANSWERS: {
      return {
        rootPage,
        resourcePage: resourcePage || SettingsResourcePagesEnum.OVERVIEW,
        namespace: src.params.namespace,
        answerId: src.params.answerId ? Number(src.params.answerId) : undefined,
        folderId: src.params.folderId ? Number(src.params.folderId) : undefined,
        inFolder: Boolean(Number(src.params.folderId) || settingsAnswersFolderRE.test(src.path)),
      };
    }

    case SettingsRootPagesEnum.INBOXES: {
      if (resourcePage === SettingsResourcePagesEnum.NEW && /new\/inbox\/rules\/(new|edit)/.test(src.url)) {
        return {
          rootPage: SettingsRootPagesEnum.RULES,
          resourcePage: SettingsResourcePagesEnum.NEW,
          namespace: src.params.namespace,
          isInboxCreation: true,
          inboxId: t.type({inbox: t.string}).is(query) ? Number(query.inbox) : undefined,
          ruleId: src.params.ruleId ? Number(src.params.ruleId) : undefined,
          recipe: src.params.recipe || undefined,
          bundleId: parseRuleBundleId(src.params.bundleId),
          filters: undefined,
        };
      }

      if (
        resourcePage === SettingsResourcePagesEnum.IMPORT_CUSTOM_ATTRIBUTES ||
        (resourcePage === SettingsResourcePagesEnum.EDIT && /business-hours/.test(src.url))
      ) {
        return {
          rootPage,
          resourcePage,
          namespace: src.params.namespace,
          inboxId: t.type({inboxId: t.string}).is(src.params) ? Number(src.params.inboxId) : undefined,
          subPage: parseSubPage(src.path, RSettingsInboxesSubPage),
        };
      }

      return parseInboxesUrl(src);
    }

    case SettingsRootPagesEnum.USER_PREFERENCES: {
      return {
        rootPage,
        namespace: src.params.namespace,
        initialPreference: t.type({search: t.string}).is(query) ? query.search : '',
      };
    }
    case SettingsRootPagesEnum.SNOOZES: {
      return {
        rootPage,
        resourcePage: resourcePage || SettingsResourcePagesEnum.OVERVIEW,
        snoozeId: src.params.snoozeId,
        namespace: src.params.namespace,
      };
    }

    case SettingsRootPagesEnum.APPLICATIONS: {
      return parseApplicationsUrl(src);
    }

    case SettingsRootPagesEnum.CIRCLES: {
      return {
        rootPage,
        resourcePage: resourcePage ?? SettingsResourcePagesEnum.OVERVIEW,
        circleId: src.params.circleId ? Number(src.params.circleId) : undefined,
      };
    }

    case SettingsRootPagesEnum.PROFILE: {
      return {
        rootPage,
      };
    }

    case SettingsRootPagesEnum.USER_HOME:
    case SettingsRootPagesEnum.ADMIN_HOME: {
      return {
        rootPage,
      };
    }

    case SettingsRootPagesEnum.APPS: {
      return parseAppsUrl(src);
    }

    case SettingsRootPagesEnum.VACATION_RESPONDERS: {
      return {
        rootPage,
        resourcePage: resourcePage || SettingsResourcePagesEnum.OVERVIEW,
        vacationResponderId: src.params.vacationResponderId,
        namespace: src.params.namespace,
      };
    }

    case SettingsRootPagesEnum.NOTIFICATIONS: {
      return {
        rootPage,
        namespace: src.params.namespace,
        resourcePage: makeNotificationsPage(src, resourcePage),
        notificationIndex: Number(src.params.notificationIndex),
        tabPage: src.path.includes('/advanced')
          ? SettingsTabsPagesEnum.ADVANCED
          : SettingsTabsPagesEnum.BASIC,
      };
    }

    case SettingsRootPagesEnum.DEVELOPERS: {
      return parseDevelopersUrl(src);
    }

    default: {
      return undefined;
    }
  }
}

function makeTabPage(query: QueryStringArguments, resourcePage: SettingsResourcePagesEnum) {
  if (resourcePage !== SettingsResourcePagesEnum.OVERVIEW) {
    return undefined;
  }

  if (!isUndefined(query.page) && !RSettingsTabsPage.is(query.page)) {
    return undefined;
  }

  return query.page || SettingsTabsPagesEnum.ALL;
}

function parseQueryParams(src: SettingsMatchProps): QueryStringArguments {
  if (!src.path.includes('?') || !src.url.includes('?')) {
    return {};
  }

  const routeQueryParams = Object.keys(parseQueryString(src.path.split('?')[1]));
  const urlSearchPart = parseQueryString(src.url.split('?')[1]);

  return _(urlSearchPart).pick(routeQueryParams).value();
}

function makeNotificationsPage(
  src: SettingsMatchProps,
  resourcePage: SettingsResourcePagesEnum | undefined,
): SettingsResourcePagesEnum {
  const {path, params} = src;

  if (params.notificationIndex) {
    return SettingsResourcePagesEnum.EDIT;
  }

  if (path.includes('/advanced/new')) {
    return SettingsResourcePagesEnum.NEW;
  }

  return resourcePage || SettingsResourcePagesEnum.OVERVIEW;
}

/**
 * Examples (^ indicates the root page we want to match):
 *
 * - /settings/tim:1/tags -> 'tags'
 * - /settings/user-home -> 'user-home'
 */
const settingsRootPageRE = pathToRegexp('/settings/:namespace?/:resourcePage', [], {start: true, end: false});

function parseRootPage(path: any): SettingsRootPagesEnum | undefined {
  if (!isString(path)) {
    return undefined;
  }

  const rootPageMatch = settingsRootPageRE.exec(path);
  if (!rootPageMatch) {
    return undefined;
  }

  const maybeRootMatch = compact([rootPageMatch[1], rootPageMatch[2]]).find((rootMatch) =>
    RSettingsRootPage.is(rootMatch),
  );

  if (!maybeRootMatch) {
    return undefined;
  }

  return maybeRootMatch as SettingsRootPagesEnum;
}

/**
 * Will match `new` in the following pattern
 * /settings/:namespace/tags/new
 */
const resourcePageInsideResourceRE = pathToRegexp(
  '/settings/:namespace/:resource/:resourceId?/:rootPage',
  [],
  {
    start: true,
    end: false,
  },
);
/**
 * Will match `new` in the follow pattern
 * /settings/:namespace/new
 */
const resourcePageAfterRootRE = pathToRegexp('/settings/:namespace/:resourcePage/:rootPage', [], {
  start: true,
  end: false,
});

const resourcePageInsideAnswersFoldersRE = pathToRegexp(
  '/settings/:namespace/answers/folders/:folderId/:resourcePage',
  [],
  {start: true, end: false},
);

function parseResourcePage(path: any): SettingsResourcePagesEnum | undefined {
  if (!isString(path)) {
    return undefined;
  }

  const resourcePageMatch =
    resourcePageInsideAnswersFoldersRE.exec(path) ||
    resourcePageInsideResourceRE.exec(path) ||
    resourcePageAfterRootRE.exec(path);

  if (!resourcePageMatch) {
    return undefined;
  }

  if (RSettingsResourcePage.is(resourcePageMatch[4])) {
    return resourcePageMatch[4];
  }

  // Rules editor follows `/edit/ruleId` instead of `/ruleId/edit`
  if (RSettingsResourcePage.is(resourcePageMatch[3])) {
    return resourcePageMatch[3];
  }

  // For tools.
  if (RSettingsResourcePage.is(resourcePageMatch[2])) {
    return resourcePageMatch[2];
  }

  return undefined;
}

function looselyParseResourcePage(path: any): SettingsResourcePagesEnum | undefined {
  if (!isString(path)) {
    return undefined;
  }

  const pathFragments = path.split('/').filter(Boolean);
  const idOrResourceOrNull = pathFragments.slice(-1)[0];
  const resourceOrNull = pathFragments.slice(-2)[0];

  if (RSettingsResourcePage.is(idOrResourceOrNull)) {
    return idOrResourceOrNull;
  }

  if (RSettingsResourcePage.is(resourceOrNull)) {
    return resourceOrNull;
  }

  return undefined;
}

/**
 * Will match the sub page in the path.
 *
 * Example:
 * /settings/tim:1/teammates/edit/ailian/rules/new -> 'rules'.
 */
const subPageRE = pathToRegexp('/settings/:namespace?/:resourcePage/:resourceId?/:rootPage/:subPage', [], {
  start: true,
  end: false,
});

function parseSubPage<REnum extends t.Mixed>(path: any, rEnum: REnum): t.TypeOf<REnum> | undefined {
  if (!isString(path)) {
    return undefined;
  }

  const subPageMatch = subPageRE.exec(path);

  if (!subPageMatch) {
    return undefined;
  }

  const subPage = subPageMatch[5];

  if (!subPage || !rEnum.is(subPage)) {
    return undefined;
  }

  return subPage;
}

const subResourceRE = pathToRegexp(
  '/settings/:namespace?/:resourcePage/:resourceId?/:rootPage/:subPage/:subResource',
  [],
  {
    start: true,
    end: false,
  },
);

/**
 * Will match `new` of the sub page.
 *
 * Example:
 * /settings/tim:1/teammates/edit/ailian/rules/edit/8 -> 'edit'
 */
function parseSubResource<REnum extends t.Mixed>(path: any, rEnum: REnum): t.TypeOf<REnum> | undefined {
  if (!isString(path)) {
    return undefined;
  }

  const subResourceMatch = subResourceRE.exec(path);

  if (!subResourceMatch) {
    return undefined;
  }

  const subResource = subResourceMatch[6];

  if (!subResource || !rEnum.is(subResource)) {
    return undefined;
  }

  return subResource;
}

function parseRuleBundleId(bundle: any): number | undefined {
  if (!isString(bundle) || bundle === 'new') {
    return undefined;
  }

  const parsedBundle = Number(bundle);

  if (!isInteger(parsedBundle)) {
    return undefined;
  }

  return parsedBundle;
}

function parseRuleFilters(
  resourcePage: SettingsResourcePagesEnum,
  query: QueryStringArguments,
): SettingsRuleListFiltersState | undefined {
  if (resourcePage !== SettingsResourcePagesEnum.OVERVIEW) {
    return undefined;
  }

  const inboxNumber = Number(query.inbox);
  const inboxId = Number.isInteger(inboxNumber) ? inboxNumber : undefined;

  return {
    query: query.query,
    categories: query.categories?.split(',').filter(RRuleBundleCategoriesEnum.is),
    onlySmart: isDefined(query.only_smart) ? query.only_smart === 'true' : undefined,
    inbox: inboxId,
  };
}

function parseApplicationsUrl(src: SettingsMatchProps): SettingsApplicationsUrl {
  const query = parseQueryParams(src);

  const baseApplicationsUrl: SettingsApplicationsBaseUrl = {
    rootPage: SettingsRootPagesEnum.APPLICATIONS,
    resourcePage: RSettingsResourcePage.is(src.params.page)
      ? src.params.page
      : SettingsResourcePagesEnum.OVERVIEW,
    searchQuery: t.type({q: t.string}).is(query) ? query.q : '',
  };

  const applicationId = Number(src.params.applicationId);

  switch (src.params.component) {
    case SettingsApplicationComponentsEnum.AUTOLINKERS: {
      const autolinkersUrl: SettingsAutolinkersUrl = {
        ...baseApplicationsUrl,
        componentPage: SettingsApplicationComponentsEnum.AUTOLINKERS,
        applicationId,
        autolinkerId: Number(src.params.id) || undefined,
      };

      return autolinkersUrl;
    }

    case SettingsApplicationComponentsEnum.OAUTH_CLIENT: {
      const oauthClientUrl: SettingsOauthClientUrl = {
        ...baseApplicationsUrl,
        componentPage: SettingsApplicationComponentsEnum.OAUTH_CLIENT,
        applicationId,
      };

      return oauthClientUrl;
    }

    case SettingsApplicationComponentsEnum.PREVIEWERS: {
      const previewersUrl: SettingsPreviewersUrl = {
        ...baseApplicationsUrl,
        componentPage: SettingsApplicationComponentsEnum.PREVIEWERS,
        applicationId,
        previewerId: Number(src.params.id) || undefined,
      };

      return previewersUrl;
    }

    case SettingsApplicationComponentsEnum.SERVERS: {
      const serversUrl: SettingsServersUrl = {
        ...baseApplicationsUrl,
        rootPage: SettingsRootPagesEnum.APPLICATIONS,
        componentPage: SettingsApplicationComponentsEnum.SERVERS,
        applicationId,
        // ID may be a number (local application server ID) or a string (installed application server origin)
        serverId: Number(src.params.id) || src.params.id,
      };

      return serversUrl;
    }

    case SettingsApplicationComponentsEnum.DETAILS: {
      const detailsUrl: SettingsApplicationsDetailsUrl = {
        ...baseApplicationsUrl,
        rootPage: SettingsRootPagesEnum.APPLICATIONS,
        componentPage: SettingsApplicationComponentsEnum.DETAILS,
        applicationId,
      };
      return detailsUrl;
    }

    default: {
      // If the resource page is new and we do not have a component page we are trying to create a new application.
      if (baseApplicationsUrl.resourcePage === SettingsResourcePagesEnum.NEW) {
        const applicationDetailsUrl: SettingsApplicationsDetailsUrl = {
          ...baseApplicationsUrl,
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.DETAILS,
          applicationId: undefined,
        };
        return applicationDetailsUrl;
      }
      const applicationDetailsUrl: SettingsApplicationsListUrl = {
        ...baseApplicationsUrl,
        rootPage: SettingsRootPagesEnum.APPLICATIONS,
        componentPage: SettingsApplicationComponentsEnum.LIST,
        applicationId: undefined,
      };

      return applicationDetailsUrl;
    }
  }
}

function parseAppsUrl(src: SettingsMatchProps): SettingsAppsUrl {
  const query = parseQueryParams(src);

  const baseAppsUrl: SettingsAppsBaseUrl = {
    rootPage: SettingsRootPagesEnum.APPS,
    searchQuery: t.type({q: t.string}).is(query) ? query.q : '',
    previousUrl: t.type({prev: t.string}).is(query) ? decodeURIComponent(query.prev) : undefined,
    source: t.type({source: t.string}).is(query) ? query.source : undefined,
  };

  const selectedTab = src.params.selectedTab || SettingsAppsTabsEnum.COLLECTIONS;
  if (!RSettingsAppsTab.is(selectedTab)) {
    return {
      ...baseAppsUrl,
      tabPage: SettingsAppsTabsEnum.COLLECTIONS,
    };
  }

  switch (selectedTab) {
    case SettingsAppsTabsEnum.CATEGORIES: {
      return {
        ...baseAppsUrl,
        tabPage: SettingsAppsTabsEnum.CATEGORIES,
        category:
          src.params.category && RIntegrationTypeCategory.is(src.params.category) ? src.params.category : '',
      };
    }

    case SettingsAppsTabsEnum.DETAILS: {
      return {
        ...baseAppsUrl,
        tabPage: SettingsAppsTabsEnum.DETAILS,
        appId: src.params.appId,
        detailsPage: src.params.page ?? SettingsAppsAppDetailsTabsEnum.OVERVIEW,
        shouldOpenConfiguration: Boolean(t.type({open_configuration: t.string}).is(query)),
      };
    }

    case SettingsAppsTabsEnum.POPULAR:
    case SettingsAppsTabsEnum.PLUGINS:
    case SettingsAppsTabsEnum.CHANNELS:
    case SettingsAppsTabsEnum.ENABLED:
    case SettingsAppsTabsEnum.ALL: {
      return {
        ...baseAppsUrl,
        tabPage: selectedTab,
      };
    }

    case SettingsAppsTabsEnum.COLLECTIONS:
    default: {
      return {
        ...baseAppsUrl,
        tabPage: SettingsAppsTabsEnum.COLLECTIONS,
      };
    }
  }
}

function parseInboxesUrl(src: SettingsMatchProps): SettingsInboxesUrl | undefined {
  const resourcePage = parseResourcePage(src.path) || SettingsResourcePagesEnum.OVERVIEW;

  if (!resourcePage) {
    return undefined;
  }

  return {
    rootPage: SettingsRootPagesEnum.INBOXES,
    resourcePage,
    namespace: src.params.namespace,
    inboxId: t.type({inboxId: t.string}).is(src.params) ? Number(src.params.inboxId) : undefined,
    channelId: t.type({channelId: t.string}).is(src.params) ? Number(src.params.channelId) : undefined,
    subPage: parseSubPage(src.path, RSettingsInboxesSubPage),
  };
}

function parseDevelopersUrl(src: SettingsMatchProps): SettingsDevelopersUrl {
  const baseDevelopersUrl: SettingsBaseDevelopersUrl = {
    rootPage: SettingsRootPagesEnum.DEVELOPERS,
  };

  const {subPage, appId, detailsPage, tokenId} = src.params;

  // If we do not have a selected subPage but an application id, we are in a lower level page.
  if (!subPage && appId && Number(appId)) {
    const baseDetailsPage: SettingsBaseDevelopersApplicationUrl = {
      ...baseDevelopersUrl,
      applicationId: Number(appId),
    };
    switch (detailsPage || 'details') {
      case 'features':
        return {
          ...baseDetailsPage,
          subPage: SettingsDevelopersPagesEnum.APPLICATION_FEATURES,
          feature: RLocalApplicationFeature.is(src.params.feature ?? '') ? src.params.feature : undefined,
        };
      case 'oauth':
        return {
          ...baseDetailsPage,
          subPage: SettingsDevelopersPagesEnum.APPLICATION_OAUTH,
        };
      case 'servers':
        return {
          ...baseDetailsPage,
          subPage: SettingsDevelopersPagesEnum.APPLICATION_SERVERS,
          serverId: Number(src.params.serverId) || undefined,
          isConnecting: Boolean(src.params.page === 'connect'),
        };
      case 'details':
      default:
        return {
          ...baseDetailsPage,
          subPage: SettingsDevelopersPagesEnum.APPLICATION_INFO,
        };
    }
  }

  // If we have a selected sub page and it is valid, return it here.
  if (
    subPage &&
    (subPage === SettingsDevelopersPagesEnum.APPLICATIONS || subPage === SettingsDevelopersPagesEnum.TOKENS)
  ) {
    return {
      ...baseDevelopersUrl,
      subPage,
      tokenId,
    };
  }

  // Fallback, default to the list applications page.
  return {
    ...baseDevelopersUrl,
    subPage: SettingsDevelopersPagesEnum.APPLICATIONS,
  };
}
